import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { of as observableOf, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IAddSpeakers, IUpdateSpeakers } from './speakers-model';

@Injectable()
export class SpeakersService {
    constructor(private http: HttpClient) {}

    private static get URL(): string {
        return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`;
    }
    private static readonly ENDPOINT =
        'events/{eventId}/eventpages/speakers/';

    /** GET event-level speakers (Speaker.EventId), independent of any page. Used to display the
     *  event's existing speakers when creating a speakers page. Returns { data: { speakers: [...] } }. */
    public getEventSpeakers(
        eventId: string
    ): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<any>>(
                SpeakersService.URL + `events/${eventId}/speakers`
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** GET */
    public getSpeakers(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<any>>(
                SpeakersService.URL +
                SpeakersService.ENDPOINT.replace('{eventId}', eventId) +
                    id
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** POST */
    public addSpeakers(
        eventId: string,
        speakersAdd: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();

        speakersAdd.speakers.forEach((speaker, index) => {
            formData.append(`speakers[${index}].index`, JSON.stringify(index));
            formData.append(`speakers[${index}].isActive`, speaker.isActive);

            if (speaker.speakerNameTranslations && speaker.speakerNameTranslations.length > 0) {
                speaker.speakerNameTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerNameTranslations[${index2}].languageId`,translation.languageId);
                    formData.append(`speakers[${index}].speakerNameTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.speakerTitleTranslations && speaker.speakerTitleTranslations.length > 0) {
                speaker.speakerTitleTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerTitleTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerTitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.speakerCompanyTranslations && speaker.speakerCompanyTranslations.length > 0) {
                speaker.speakerCompanyTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerCompanyTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerCompanyTranslations[${index2}].value`, translation.value);
                });
            }

            // Handle speakerBioTranslations
            if (speaker.speakerBioTranslations && speaker.speakerBioTranslations.length > 0) {
                speaker.speakerBioTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerBioTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerBioTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.image) {
                formData.append(`speakers[${index}].image`, speaker.image);
            }
            if (speaker.id) {
                formData.append(`speakers[${index}].id`, speaker.id);
            }
        });

        speakersAdd.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        speakersAdd.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const speakersPageDisplayType = JSON.stringify(
            speakersAdd.speakersPageDisplayType
        );

        formData.append('speakersPageDisplayType', speakersPageDisplayType);
        // The create form preloads and submits the event's full speaker set, so the backend may remove
        // event-level speakers the user deleted (reconcile like edit). Older cached bundles omit this flag.
        formData.append('manageSpeakers', 'true');

        return this.http
            .post<SuccessResponse<boolean>>(
                SpeakersService.URL +
                SpeakersService.ENDPOINT.replace('{eventId}', eventId),
                formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public updateSpeakers(
        eventId: string,
        id: string,
        speakersUpdate: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();

        speakersUpdate.speakers.forEach((speaker, index) => {
            formData.append(`speakers[${index}].index`, JSON.stringify(index));
            formData.append(`speakers[${index}].isActive`, speaker.isActive);

            if (speaker.speakerNameTranslations && speaker.speakerNameTranslations.length > 0) {
                speaker.speakerNameTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerNameTranslations[${index2}].languageId`,translation.languageId);
                    formData.append(`speakers[${index}].speakerNameTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.speakerTitleTranslations && speaker.speakerTitleTranslations.length > 0) {
                speaker.speakerTitleTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerTitleTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerTitleTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.speakerCompanyTranslations && speaker.speakerCompanyTranslations.length > 0) {
                speaker.speakerCompanyTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerCompanyTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerCompanyTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.speakerBioTranslations && speaker.speakerBioTranslations.length > 0) {
                speaker.speakerBioTranslations.forEach((translation, index2) => {
                    formData.append(`speakers[${index}].speakerBioTranslations[${index2}].languageId`, translation.languageId);
                    formData.append(`speakers[${index}].speakerBioTranslations[${index2}].value`, translation.value);
                });
            }

            if (speaker.image) {
                formData.append(`speakers[${index}].image`, speaker.image);
            }
            if (speaker.imageUrl && !speaker.image) {
                formData.append(`speakers[${index}].imageUrl`, speaker.imageUrl);
            }
            if (speaker.id) {
                formData.append(`speakers[${index}].id`, speaker.id);
            }
        });

        speakersUpdate.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        speakersUpdate.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const speakersPageDisplayType = JSON.stringify(
            speakersUpdate.speakersPageDisplayType
        );

        formData.append('speakersPageDisplayType', speakersPageDisplayType);

        return this.http
            .put<SuccessResponse<boolean>>(
                SpeakersService.URL +
                SpeakersService.ENDPOINT.replace('{eventId}', eventId) +
                    id, formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public enableSpeakers(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                SpeakersService.URL +
                SpeakersService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/activate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public disableSpeakers(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                SpeakersService.URL +
                SpeakersService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/deactivate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }
}
